/**
 * Systems Registry
 * 
 * Global registry for Processes and ViewKinds.
 * The allocator arranges all active ViewInstances, regardless of shape or count,
 * and the renderer delegates drawing based on view.kind.
 * 
 * This registry enables topology-agnostic architecture where:
 * - Processes can be registered and discovered
 * - ViewKinds define available rendering types
 * - The system can extend to 3D or procedural domains without redesign
 */

import type { Process } from './process.js';
import type { ViewInstance } from './process.js';

/**
 * Process factory function
 */
export type ProcessFactory = (id: string) => Process;

/**
 * View factory function
 */
export type ViewFactory = (id: string, kind: ViewInstance['kind'], data?: unknown) => ViewInstance;

/**
 * Systems registry
 */
class SystemsRegistry {
  private processes: Map<string, ProcessFactory> = new Map();
  private viewKinds: Map<string, ViewFactory> = new Map();

  /**
   * Register a process factory
   */
  registerProcess(kind: string, factory: ProcessFactory): void {
    this.processes.set(kind, factory);
  }

  /**
   * Register a view kind factory
   */
  registerViewKind(kind: ViewInstance['kind'], factory: ViewFactory): void {
    this.viewKinds.set(kind, factory);
  }

  /**
   * Create a process instance
   */
  createProcess(kind: string, id: string): Process | null {
    const factory = this.processes.get(kind);
    if (!factory) return null;
    return factory(id);
  }

  /**
   * Create a view instance
   */
  createView(kind: ViewInstance['kind'], id: string, data?: unknown): ViewInstance | null {
    const factory = this.viewKinds.get(kind);
    if (!factory) return null;
    return factory(id, kind, data);
  }

  /**
   * Get all registered process kinds
   */
  getProcessKinds(): string[] {
    return Array.from(this.processes.keys());
  }

  /**
   * Get all registered view kinds
   */
  getViewKinds(): ViewInstance['kind'][] {
    return Array.from(this.viewKinds.keys()) as ViewInstance['kind'][];
  }

  /**
   * Check if a process kind is registered
   */
  hasProcess(kind: string): boolean {
    return this.processes.has(kind);
  }

  /**
   * Check if a view kind is registered
   */
  hasViewKind(kind: ViewInstance['kind']): boolean {
    return this.viewKinds.has(kind);
  }
}

/**
 * Global systems registry instance
 */
export const registry = new SystemsRegistry();

/**
 * Register a process factory
 */
export function registerProcess(kind: string, factory: ProcessFactory): void {
  registry.registerProcess(kind, factory);
}

/**
 * Register a view kind factory
 */
export function registerViewKind(kind: ViewInstance['kind'], factory: ViewFactory): void {
  registry.registerViewKind(kind, factory);
}

/**
 * Create a process instance
 */
export function createProcess(kind: string, id: string): Process | null {
  return registry.createProcess(kind, id);
}

/**
 * Create a view instance
 */
export function createView(kind: ViewInstance['kind'], id: string, data?: unknown): ViewInstance | null {
  return registry.createView(kind, id, data);
}

/**
 * Get all registered process kinds
 */
export function getProcessKinds(): string[] {
  return registry.getProcessKinds();
}

/**
 * Get all registered view kinds
 */
export function getViewKinds(): ViewInstance['kind'][] {
  return registry.getViewKinds();
}

